import React, { useContext, useState, useEffect } from 'react';
import {
  canUseWaiverReports,
  collectGarbage,
  setHelpPage,
  setWindowTitle,
  goTo
} from '../../configTdm';
import moment from 'moment';
import _ from 'lodash';
import WaiverReportingForm from './WaiverReportingForm';
import WaiverReportResults from './WaiverReportResults';
import { getProperty } from "../../fsConfig";
import { waiverReportingService } from '../reportingService';
import { useNavigate } from 'react-router-dom';
import { PageContext } from '../../pages/components/page';
import FullPageLoadingSpinner from '../../pages/components/fullPageLoadingSpinner';

interface WaiverReportProps { }

interface Report {
  summary: any;
  waiversByAirlineAndMonth: any;
  waiverMatchesByAirlineAndMonth: any;
  waiversByAirportAndMonth: any;
  waiverMatchesByAirportAndMonth: any;
}

interface Csv {
  data: string;
  name: string;
  startDate: string;
  endDate: string;
  duration: string;
}

const WaiverReport: React.FC<WaiverReportProps> = () => {
  const defaultTime = () =>
    moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
  const [title] = useState('WAIVER REPORTING');
  const [hasReport, setHasReport] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [csv, setCsv] = useState<Csv | null>(null);
  const [reportDateRange, setReportDateRange] = useState<string | null>(null);
  const [accordionOpen, setAccordionOpen] = useState(true);
  const [searching, setSearching] = useState(false);
  const [startTime, setStartTime] = useState(defaultTime());
  const [endTime, setEndTime] = useState(defaultTime());
  const [tripGroupIds, setTripGroupIds] = useState<string[]>([]);
  const [formKey, setFormKey] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const { accountName } = getProperty('session', {});
  const navigate = useNavigate();
  const { pushAlert }: { pushAlert: (type: string, message: string) => void } = useContext(PageContext);

  useEffect(() => {
    if (!canUseWaiverReports()) {
      goTo('/home');
    }

    setWindowTitle('Waiver Reporting');
    setHelpPage('helpWaiverReport');


    const initialLoadTimer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => {
      setReport(null);
      setCsv(null);
      setReportDateRange(null);
      collectGarbage();
      clearTimeout(initialLoadTimer);
    };
  }, []);

  const setTripMatchPercentage = (summary: any) => {
    if (
      _.isNumber(summary.tripsMatchingWaivers) &&
      _.isNumber(summary.tripsMonitored)
    ) {
      summary.percentageTripsMatchingWaivers =
        summary.tripsMonitored > 0
          ? Math.round(
            (summary.tripsMatchingWaivers / summary.tripsMonitored) *
            100 *
            10,
          ) / 10
          : 0;
    }
  };

  const processResults = (count: number, results: any) => {
    const startDate = moment(results.request.startTime).format('MMMM DD, YYYY');
    const endDate = moment(results.request.endTime).format('MMMM DD, YYYY');
    setReportDateRange(
      startDate === endDate ? startDate : `${startDate} through ${endDate}`,
    );

    let summary: any = _.pick(results, [
      'waiversIssued',
      'tripsMatchingWaivers',
      'waiversByAirline',
      'mostAffectedAirports',
    ]);

    summary.tripsMonitored = count;
    setTripMatchPercentage(summary);

    setReport({
      summary,
      waiversByAirlineAndMonth: results.waiversByAirlineAndMonth,
      waiverMatchesByAirlineAndMonth: results.waiverMatchesByAirlineAndMonth,
      waiversByAirportAndMonth: results.waiversByAirportAndMonth,
      waiverMatchesByAirportAndMonth: results.waiverMatchesByAirportAndMonth,
    });

    setCsv({
      data: 'results.csv',
      name: 'waiverReport',
      startDate: results.request.startTime,
      endDate: results.request.endTime,
      duration: results.request.duration,
    });
    setHasReport(true);
  };

  const handleReport = async (searchRequest: any) => {
    setLoading(true);
    try {
      const [tripCountResponse, reportResponse] = await Promise.all([
        waiverReportingService.tripCount(searchRequest),
        waiverReportingService.runReport(searchRequest)
      ]);

      if (tripCountResponse && tripCountResponse.data && reportResponse?.status != null) {
        const count = tripCountResponse.data.count;
        const results = reportResponse.data;
        results.request = searchRequest;
        processResults(count, results);
      }
      setAccordionOpen(false);
      setSearching(false);
    } catch (error) {
      console.error('Error processing report:', error);
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const submitForm = (formIsValid: boolean) => {
    const error = validate(formIsValid);
    if (error) {
      pushAlert('danger', error);
      return;
    }

    if (searching) {
      return;
    }

    const searchRequest = buildSearchRequest();
    setSearching(true);
    handleReport(searchRequest);
  };

  const validate = (formIsValid: boolean): string | undefined => {
    if (!formIsValid) {
      return 'Reporting criteria is invalid';
    }
    if (!startTime || !endTime) {
      return 'Invalid date format';
    }
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);
    if (!startMoment.isValid() && !endMoment.isValid()) {
      return 'Invalid Start Month and End Month';
    }
    if (!startMoment.isValid()) {
      return 'Invalid Start Month';
    }
    if (!endMoment.isValid()){
      return 'Invalid End Month';
    }
    if (endMoment.isBefore(startMoment)) {
      return 'End time must be after start time';
    }
    if (endMoment.diff(startMoment, 'years', true) > 1) {
      return 'Maximum one year range supported';
    }
    return;
  };

  const buildSearchRequest = () => {
    const endTimeFormatted = moment(endTime).endOf('month').format('YYYY-MM-DD');
    return {
      tripGroupIds: tripGroupIds,
      startTime: moment(startTime).format('YYYY-MM-DD'),
      endTime: moment(endTimeFormatted).format('YYYY-MM-DD'),
      clientOffset: moment().format('Z'),
    };
  };

  const resetForm = () => {
    setStartTime(defaultTime());
    setEndTime(defaultTime());
    setTripGroupIds([]);
    setFormKey(Date.now());
  };

  return (
    <div className="row">
      {loading && <FullPageLoadingSpinner />} {/* Conditionally render the spinner */}
      <div className="row search-title">
        <div className="col-md-12">{title}</div>
      </div>
      <WaiverReportingForm
        accordionOpen={accordionOpen}
        setAccordionOpen={setAccordionOpen}
        startTime={startTime}
        setStartTime={setStartTime}
        endTime={endTime}
        setEndTime={setEndTime}
        formKey={formKey}
        submitForm={submitForm}
        resetForm={resetForm}
      />
      <div className="row report-results">
        <div className="col-md-12 results-header-container">
          <div className="report-results col-md-3"></div>
          <div className="report-results col-md-6"></div>
          <div className="report-results col-md-3"></div>
        </div>
      </div>
      {hasReport && (
        <div id="report-results-set" className="row report-results">
          <div className="col-md-12 results-header-container">
            <div className="col-md-12 results-header">
              <div className="col-md-6 main-title">
                WAIVER REPORT
              </div>
            </div>
            <div className="col-md-12">
              <div className="col-md-4 sub-main-title">
                Report for {reportDateRange}
              </div>
              <div className="col-md-4 sub-main-sub-title text-center">
                Waiver Report by {accountName}
              </div>
              <div className="col-md-4 sub-title">
                Powered by Cirium
              </div>
            </div>
          </div>
          {report && <WaiverReportResults report={report} />}
        </div>
      )}
    </div>
  );
};

export default WaiverReport;